import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import DivKitCard from './DivKitCard';

interface Message {
  id: string;
  role: string;
  message_text: string;
  timestamp: Date;
  message_type?: string;
  payload?: any;
  action_type?: string;
  command_type?: string;
  control_type?: string;
  sequence_id?: string;
  full_content?: any;
}

type JSONValue = string | number | boolean | null | JSONObject | JSONArray;

interface JSONObject {
  [key: string]: JSONValue;
}

interface JSONArray extends Array<JSONValue> {}
type SessionData = JSONObject;

interface AppStatus {
  health: boolean;
  ready: boolean;
}

const API_BASE_URL = (window as any).runtimeConfig?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const WEBHOOK_BASE_URL = (window as any).runtimeConfig?.VITE_WEBHOOK_BASE_URL || import.meta.env.VITE_WEBHOOK_BASE_URL || 'http://localhost:3001';
const CUSTOM_AGENT_BRIDGE_URL = (window as any).runtimeConfig?.VITE_CUSTOM_AGENT_BRIDGE_URL || import.meta.env.VITE_CUSTOM_AGENT_BRIDGE_URL || WEBHOOK_BASE_URL;

// Configure axios interceptor to add custom agent bridge URL header if set
if (CUSTOM_AGENT_BRIDGE_URL) {
  axios.interceptors.request.use((config) => {
    config.headers['X-Custom-Agent-Bridge-Url'] = CUSTOM_AGENT_BRIDGE_URL;
    return config;
  });
}

const App: React.FC = () => {
  const [status, setStatus] = useState<AppStatus>({ health: false, ready: false });
  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [rawMessages, setRawMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [messageLanguage, setMessageLanguage] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventPayloadText, setEventPayloadText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'chat' | 'raw'>('chat');
  const [showOnlyTextMessages, setShowOnlyTextMessages] = useState(true);
  
  // Session creation form state
  const [sessionType, setSessionType] = useState<'v1' | 'v2' | 'v2-local'>('v2-local');
  const [autorun, setAutorun] = useState(false);
  const [sessionData, setSessionData] = useState<SessionData>({'user_name': 'Niamh', 'user_id': 'user_1'});
  const [language, setLanguage] = useState<string>('ru');
  const [startAgent, setStartAgent] = useState<string>('RAG_Agent_RU');
  const [autoFirstMessage, setAutoFirstMessage] = useState<string>('');
  const [agentName, setAgentName] = useState<string>('');
  const [agentsOverrides, setAgentsOverrides] = useState<{[key: string]: any}>({});
  const [agentsOverridesRawText, setAgentsOverridesRawText] = useState<string | null>(null);
  
  // V1 specific state
  const [scenarioData, setScenarioData] = useState<any>(null);
  const [scenarioLoaded, setScenarioLoaded] = useState<boolean>(false);
  
  // V2 scenario specific state
  const [scenarioDataV2, setScenarioDataV2] = useState<any>(null);
  const [scenarioLoadedV2, setScenarioLoadedV2] = useState<boolean>(false);
  const [useExistingScenarioV2, setUseExistingScenarioV2] = useState<boolean>(false);
  const [existingScenarioIdV2, setExistingScenarioIdV2] = useState<string>('');
  
  // Auto-creation state
  const [shouldAutoCreate, setShouldAutoCreate] = useState<boolean>(false);
  const [isDirectScenarioLink, setisDirectScenarioLink] = useState<boolean>(false);
  
  // Conversation data state (for pre-loading before session creation)
  const [conversationData, setConversationData] = useState<any[]>([]);
  const [conversationLoaded, setConversationLoaded] = useState<boolean>(false);
  
  // Tools metadata state
  const [toolsMetadata, setToolsMetadata] = useState<any[]>([
    {
      name: 'rag_tool',
      tools_metadata: {
        RAG_Agent_RU: {
          mcp_tool_name: 'tool_rag_support_lola_direct',
          mcp_tool_version: 'latest',
          collection_id: '',
          output_document_id: false,
          output_document_score: false,
          document_score_threshold: 0.5,
          rag_service_unavailable_message: 'Document storage is not accessible right now',
          no_documents_found_message: 'No relevant documents found'
        },
        RAG_Agent_UZ: {
          mcp_tool_name: 'tool_rag_support_lola_direct',
          mcp_tool_version: 'latest',
          collection_id: '',
          output_document_id: false,
          output_document_score: false,
          document_score_threshold: 0.5,
          rag_service_unavailable_message: 'Document storage is not accessible right now',
          no_documents_found_message: 'No relevant documents found'
        }
      }
    },
    {
      name: 'call_human_operator_tool',
      tools_metadata: {
        RAG_Agent_RU: {
          emulate_operator_switch: true,
          send_switch_message: false,
          switch_messages: [
            "Пожалуйста, опишите вашу проблему. Оператор скоро присоединится.",
            "Я подключаю вас к оператору. Пожалуйста, опишите вашу проблему, пока он присоединяется.",
          ]
        },
        RAG_Agent_UZ: {
          emulate_operator_switch: true,
          send_switch_message: false,
          switch_messages: [
            "Iltimos, muammoingizni tasvirlab bering. Operator tez orada qo'shiladi.",
            "Sizni operatorga bog'layapman. U qo'shilayotgan paytda muammoingizni tasvirlab bering."
          ]
        }
      }
    },
    {
      name: 'request_contact_tool',
      tools_metadata: {
        Sales_Agent_RU: {
          button_text: "Отправить номер",
          text: "Поделитесь номером телефона"
        },
        Sales_Agent_UZ: {
          button_text: "Raqamni yuborish",
          text: "Iltimos, telefon raqamingizni ulashing"
        }
      }
    }
  ]);

  // Raw text state for metadata fields to allow typing without immediate validation
  const [metadataRawText, setMetadataRawText] = useState<{[key: string]: string}>({});
  
  // Raw text state for sessionData to allow typing without immediate validation
  const [sessionDataRawText, setSessionDataRawText] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check app status on component mount and periodically
  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Focus input on component mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Focus input when session becomes active
  useEffect(() => {
    if (sessionId) {
      inputRef.current?.focus();
    }
  }, [sessionId]);

  // Focus input when loading completes
  useEffect(() => {
    if (!loading && sessionId) {
      inputRef.current?.focus();
    }
  }, [loading, sessionId]);

  // Auto-create session from URL parameter - step 1: detect URL param and set state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const scenarioIdParam = urlParams.get('scenario_id');
    
    if (scenarioIdParam && !sessionId && !loading) {
      // Set session type to v2 and configure for existing scenario
      setSessionType('v2');
      setUseExistingScenarioV2(true);
      setExistingScenarioIdV2(scenarioIdParam);
      setShouldAutoCreate(true);
      setisDirectScenarioLink(true);
    }
  }, []); // Run only once on mount

  // Auto-create session from URL parameter - step 2: create session when state is ready
  useEffect(() => {
    if (shouldAutoCreate && sessionType === 'v2' && useExistingScenarioV2 && existingScenarioIdV2 && !sessionId && !loading) {
      setShouldAutoCreate(false); // Prevent multiple calls
      createSession();
    }
  }, [shouldAutoCreate, sessionType, useExistingScenarioV2, existingScenarioIdV2, sessionId, loading]);

  const checkStatus = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/v1/health`),
        axios.get(`${API_BASE_URL}/api/v1/ready`)
      ]);
      setStatus({
        health:
          results[0].status === 'fulfilled' && (results[0] as PromiseFulfilledResult<any>).value.status === 200,
        ready:
          results[1].status === 'fulfilled' && (results[1] as PromiseFulfilledResult<any>).value.status === 200,
      });
    } catch (error) {
      console.error('Error checking status:', error);
      setStatus({ health: false, ready: false });
    }
  };

  // Helper function to clean up rich text editor artifacts
  const unescapeRichTextArtifacts = (text: string): string => {
    if (typeof text !== 'string') return text;
    
    // Replace double-escaped newlines with single newlines
    text = text.replace(/\\\n/g, '\n');
    
    // Replace escaped brackets
    text = text.replace(/\\\[/g, '[');
    text = text.replace(/\\\]/g, ']');
    
    // Replace double backslashes with single backslashes
    text = text.replace(/\\\\/g, '\\');
    
    return text;
  };

  const loadScenarioFile = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate required fields
      if (!data.id || !data.settings || !data.engineMetadata) {
        throw new Error('Invalid scenario file format');
      }
      
      // Clean up rich text artifacts from scenario parts
      if (data.engineMetadata?.prompt) {
        const prompt = data.engineMetadata.prompt;
        if (prompt.role) prompt.role = unescapeRichTextArtifacts(prompt.role);
        if (prompt.goal) prompt.goal = unescapeRichTextArtifacts(prompt.goal);
        if (prompt.example) prompt.example = unescapeRichTextArtifacts(prompt.example);
        if (prompt.flow) prompt.flow = unescapeRichTextArtifacts(prompt.flow);
        if (prompt.success) prompt.success = unescapeRichTextArtifacts(prompt.success);
        if (prompt.prompt_reminder) prompt.prompt_reminder = unescapeRichTextArtifacts(prompt.prompt_reminder);
      }
      
      setScenarioData(data);
      setScenarioLoaded(true);
      setError('');
      console.log('Loaded scenario:', data.name || data.id);
    } catch (error: any) {
      setError(`Failed to load scenario file: ${error.message}`);
      setScenarioLoaded(false);
    }
  };

  const handleScenarioFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadScenarioFile(file);
    }
  };

  const loadScenarioFileV2 = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate required fields for V2 genTools format
      if (!data.id || !data.engineMetadata?.genTools) {
        throw new Error('Invalid V2 scenario file format. Missing genTools structure.');
      }
      
      const genTools = data.engineMetadata.genTools;
      if (!genTools.settings || !genTools.agents || genTools.agents.length === 0) {
        throw new Error('Invalid V2 scenario file format. Missing settings or agents.');
      }
      
      setScenarioDataV2(data);
      setScenarioLoadedV2(true);
      setError('');
      console.log('Loaded V2 scenario:', data.name || data.id);
    } catch (error: any) {
      setError(`Failed to load V2 scenario file: ${error.message}`);
      setScenarioLoadedV2(false);
    }
  };

  const handleScenarioFileUploadV2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadScenarioFileV2(file);
    }
  };

  const loadConversationFile = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate the file is an array
      if (!Array.isArray(data)) {
        throw new Error('Invalid conversation file format. Expected array of messages: [{"role": "...", "content": "..."}, ...]');
      }
      
      setConversationData(data);
      setConversationLoaded(true);
      setError('');
      console.log('Loaded conversation with', data.length, 'messages');
    } catch (error: any) {
      setError(`Failed to load conversation file: ${error.message}`);
      setConversationLoaded(false);
    }
  };

  const handleConversationFileUploadPreSession = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadConversationFile(file);
    }
  };

  const createSession = async () => {
    try {
      setLoading(true);
      setError('');
      
      const newSessionId = uuidv4();
      
      if (sessionType === 'v1') {
        // Validate v1 requirements
        if (!scenarioLoaded || !scenarioData) {
          setError('Please load a scenario file first');
          setLoading(false);
          return;
        }
        
        // Step 1: Create/update scenario
        const scenarioPayload = {
          scenario_id: scenarioData.id,
          agent_type: scenarioData.settings.agentType,
          language: scenarioData.settings.language,
          scenario_parts: {
            role: scenarioData.engineMetadata.prompt.role || '',
            goal: scenarioData.engineMetadata.prompt.goal || '',
            example: scenarioData.engineMetadata.prompt.example || '',
            flow: scenarioData.engineMetadata.prompt.flow || '',
            success: scenarioData.engineMetadata.prompt.success || '',
            prompt_reminder: scenarioData.engineMetadata.prompt.prompt_reminder || null,
          },
          information_sources: scenarioData.engineMetadata.prompt.informationSources || null,
          default_session_data: scenarioData.engineMetadata.prompt.defaultSessionData || null,
        };
        
        await axios.post(`${API_BASE_URL}/api/v1/scenarios`, scenarioPayload);
        
        // Step 2: Create session with scenario_id
        const payloadV1: any = {
          version: "v1",
          scenario_id: scenarioData.id,
          autorun: autorun,
          language: scenarioData.settings.language,
          channel_type: "text",
        };

        // Add session_data if provided (will merge with scenario defaults)
        if (Object.keys(sessionData).length > 0) {
          payloadV1.session_data = sessionData;
        }
        
        const requestBodyV1: any = {
          session_id: newSessionId,
          payload: payloadV1,
          agent_name: agentName || null,
          conversation_token: uuidv4(),
        };
        
        await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV1);
        
      } else if (sessionType === 'v2') {
        // V2 scenario-based session creation
        
        if (useExistingScenarioV2) {
          // Using existing scenario_id - skip scenario upload
          if (!existingScenarioIdV2.trim()) {
            setError('Please provide a scenario_id');
            setLoading(false);
            return;
          }
          
          // Create session with existing scenario_id directly
          const payloadV2: any = {
            version: "v2",
            scenario_id: existingScenarioIdV2,
            ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
          };

          // If this is a URL-based session, only send minimal payload
          // The scenario config already has all settings, so don't override them
          if (!isDirectScenarioLink) {
            // For manually created sessions, include the UI form values
            payloadV2.autorun = autorun;
            payloadV2.language = language;

            // Add session_data if provided (will merge with scenario defaults)
            if (Object.keys(sessionData).length > 0) {
              payloadV2.session_data = sessionData;
            }
          }

          const requestBodyV2: any = {
            session_id: newSessionId,
            payload: payloadV2,
            conversation_token: uuidv4(),
            auto_first_message: autoFirstMessage || undefined,
          };

          await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2);
          
        } else {
          // Upload new scenario file
          // Validate v2 scenario requirements
          if (!scenarioLoadedV2 || !scenarioDataV2) {
            setError('Please load a V2 scenario file first');
            setLoading(false);
            return;
          }
          
          const genTools = scenarioDataV2.engineMetadata.genTools;
          const settings = genTools.settings;
          const agents = genTools.agents;
          const analytics = genTools.analytics || [];
          
          // Create mapping from agent ID to agent name
          const agentIdToName: { [key: string]: string } = {};
          agents.forEach((agent: any) => {
            const agentId = agent.id || '';
            const agentName = agent.agent_name || agentId;
            if (agentId) {
              agentIdToName[agentId] = agentName;
            }
          });
          
          // Convert start_agent from ID to name if needed
          let startAgentName = settings.start_agent || '';
          if (startAgentName && agentIdToName[startAgentName]) {
            startAgentName = agentIdToName[startAgentName];
          }
          
          // Extract prephrases - use PrephrasesConfig if latency/probability are available
          const prephrasesList = settings.prephrases || [];
          const prephrasesLatency = settings.prephrases_latency;
          const prephrasesProbability = settings.prephrases_probability;
          
          let prephrasesConfig: any;
          if (prephrasesLatency !== undefined || prephrasesProbability !== undefined) {
            // Create PrephrasesConfig object
            prephrasesConfig = {
              prephrases: prephrasesList,
              latency: prephrasesLatency !== undefined ? prephrasesLatency : 1000,
              probability: prephrasesProbability !== undefined ? prephrasesProbability : 0.9,
            };
          } else {
            // Use simple list if no latency/probability
            prephrasesConfig = prephrasesList;
          }
          
          // Step 1: Create/update scenario via V2 API
          const scenarioPayloadV2: any = {
            scenario_id: scenarioDataV2.id,
            autorun: settings.autorun !== undefined ? settings.autorun : autorun,
            language: settings.language || language,
            timezone: settings.timezone || null,
            agent_type: settings.agent_type || 'voice',
            stream_method: settings.stream_method || 'sentence_stream',
            start_agent: startAgentName,
            prephrases: prephrasesConfig,
            default_session_data: settings.default_session_data || null,
            default_tone_of_voice: settings.default_tone_of_voice || null,
            reactivation_delay_intervals: settings.reactivation_delay_intervals || [],
            tools_prephrases: settings.tools_prephrases || {},
            agents: agents.map((agent: any) => {
              // Convert handoff_agents from IDs to names
              const handoffAgentIds = agent.handoff_agents || [];
              const handoffAgentNames = handoffAgentIds.map((handoffId: string) => {
                // If it's already a name (not an ID), keep it as is
                // Otherwise, try to convert from ID to name
                return agentIdToName[handoffId] || handoffId;
              });
              
              return {
                agent_name: agent.agent_name || agent.id || '',
                inference_preset: agent.inference_preset ?? null,
                agent_class: agent.agent_class ?? 'CoordinatedAgent',
                prompt: agent.prompt || '',
                prompt_reminder: agent.prompt_reminder || null,
                prephrases: agent.prephrases ?? null,
                flow: agent.flow || '',
                goal: agent.goal || '',
                role: agent.role || '',
                success: agent.success || '',
                examples: agent.examples || '',
                lang_style: agent.lang_style || '',
                tools: agent.tools || [],
                handoff_agents: handoffAgentNames,
              };
            }),
            analytics: analytics.map((analyticsConfig: any) => ({
              prompt: analyticsConfig.prompt || '',
              examples: analyticsConfig.examples || '',
              targets: analyticsConfig.targets || [],
              triggers: analyticsConfig.triggers || [],
              agent_mode: analyticsConfig.agent_mode || 'classic',
              analytics_agent: analyticsConfig.analytics_agent || null,
            })),
            metadata: scenarioDataV2.version ? (() => {
              const metadataObj: { [key: string]: string } = {};
              for (const [key, value] of Object.entries(scenarioDataV2.version)) {
                metadataObj[key] = String(value);
              }
              return metadataObj;
            })() : null,
          };
          
          await axios.post(`${API_BASE_URL}/api/v2/scenarios`, scenarioPayloadV2);
          
          // Step 2: Create session with scenario_id via V1 endpoint (both endpoints use same function)
          const payloadV2: any = {
            version: "v2",
            scenario_id: scenarioDataV2.id,
            autorun: settings.autorun !== undefined ? settings.autorun : autorun,
            language: settings.language || language,
            start_agent: startAgentName,
            channel_type: "text",
            ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
          };

          // Add session_data if provided (will merge with scenario defaults)
          if (Object.keys(sessionData).length > 0) {
            payloadV2.session_data = sessionData;
          }

          const requestBodyV2: any = {
            session_id: newSessionId,
            payload: payloadV2,
            conversation_token: uuidv4(),
            auto_first_message: autoFirstMessage || undefined,
          };

          await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2);
        }
        
      } else {
        // V2-local session creation (local config-based)
        // Validate required fields
        if (!startAgent.trim()) {
          setError('Start agent is required');
          setLoading(false);
          return;
        }
        
        // Build payload for v2-local
        const payloadV2Local: any = {
          version: "v2",
          session_data: Object.keys(sessionData).length > 0 ? sessionData : undefined,
          tools_metadata: toolsMetadata.map(tool => ({
            tool_name: tool.name,
            tools_metadata: tool.tools_metadata
          })),
          language: language,
          autorun,
          start_agent: startAgent || undefined,
          channel_type: "text",
          ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
        };

        // Build request body for v2-local
        const requestBodyV2Local: any = {
          session_id: newSessionId,
          conversation_token: uuidv4(),
          payload: payloadV2Local,
          auto_first_message: autoFirstMessage || undefined,
        };

        await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2Local);
      }
      
      setSessionId(newSessionId);
      const sessionTypeLabel = sessionType === 'v2-local' ? 'v2-local' : sessionType;
      const initialMessages: Message[] = [{
        id: uuidv4(),
        role: 'system',
        message_text: `Session (${sessionTypeLabel}) created with ID: ${newSessionId}`,
        message_type: 'TextMessage',
        timestamp: new Date()
      }];

      // Register this session with our webhook server
      await axios.post(`${WEBHOOK_BASE_URL}/register-session`, {
        sessionId: newSessionId
      });

      // If conversation was pre-loaded, import it now
      if (conversationLoaded && conversationData.length > 0) {
        try {
          // Import messages to the session
          await axios.post(`${API_BASE_URL}/api/v1/sessions/${newSessionId}/messages`, {
            messages: conversationData
          });

          // Add imported messages to UI
          const importedMessages: Message[] = conversationData.map((msg: any) => ({
            id: uuidv4(),
            role: msg.role,
            message_text: msg.content,
            timestamp: new Date(),
            message_type: 'TextMessage',
          }));

          initialMessages.push({
            id: uuidv4(),
            role: 'system',
            message_text: `Imported ${conversationData.length} pre-loaded messages`,
            message_type: 'TextMessage',
            timestamp: new Date()
          });

          initialMessages.push(...importedMessages);

          // Clear the pre-loaded conversation
          setConversationData([]);
          setConversationLoaded(false);
        } catch (importError: any) {
          setError(`Session created but failed to import conversation: ${importError.response?.data?.detail || importError.message}`);
        }
      }

      setMessages(initialMessages);
      setRawMessages([]);

    } catch (error: any) {
      setError(`Failed to create session: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError('');
      
      await axios.delete(`${API_BASE_URL}/api/v1/sessions/${sessionId}`);
      
      // Unregister session from webhook server
      await axios.post(`${WEBHOOK_BASE_URL}/unregister-session`, {
        sessionId
      });

      // If this was a URL-based session, remove scenario_id from URL
      if (isDirectScenarioLink) {
        const url = new URL(window.location.href);
        url.searchParams.delete('scenario_id');
        window.history.replaceState({}, '', url.toString());
        setisDirectScenarioLink(false);
      }

      setSessionId('');
      setMessages([]);
      setRawMessages([]);
    } catch (error: any) {
      setError(`Failed to delete session: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: inputMessage,
      timestamp: new Date(),
      message_type: 'TextMessage',
    };

  setMessages((prev: Message[]) => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: "TextMessage",
        message_text: messageToSend,
        ...(messageLanguage.trim() && { agent_language: messageLanguage.trim() })
      });

    } catch (error: any) {
      setError(`Failed to send message: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
      // Focus input after sending message - use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const sendSilence = async () => {
    if (!sessionId) return;

    const silenceMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: messageLanguage.trim() ? `Silence (${messageLanguage.trim()})` : 'Silence',
      timestamp: new Date(),
      message_type: 'SilenceMessage',
    };

    setMessages((prev: Message[]) => [...prev, silenceMessage]);

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: "SilenceMessage",
        ...(messageLanguage.trim() && { agent_language: messageLanguage.trim() })
      });

    } catch (error: any) {
      setError(`Failed to send silence: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
      // Focus input after sending silence - use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const handleSend = async () => {
    if (!sessionId) return;

    if (inputMessage.trim()) {
      await sendMessage();
    } else {
      await sendSilence();
    }
  };

  const isEventPayloadValid = () => {
    if (!eventPayloadText.trim()) return true;
    try {
      JSON.parse(eventPayloadText);
      return true;
    } catch {
      return false;
    }
  };

  const sendUserEvent = async () => {
    if (!eventType.trim() || !sessionId || !isEventPayloadValid()) return;

    const payload = eventPayloadText.trim() ? JSON.parse(eventPayloadText) : undefined;
    const eventMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: eventType.trim(),
      timestamp: new Date(),
      message_type: 'UserEvent',
      payload,
    };

    setMessages((prev: Message[]) => [...prev, eventMessage]);

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: 'UserEvent',
        event_id: uuidv4(),
        event_type: eventType.trim(),
        ...(payload !== undefined && { payload }),
      });

    } catch (error: any) {
      setError(`Failed to send user event: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadConversationHistory = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE_URL}/api/v1/sessions/${sessionId}/messages`);
      
      // Create a blob from the JSON data
      const jsonBlob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      
      // Create a download link and trigger it
      const url = URL.createObjectURL(jsonBlob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `conversation-${sessionId.slice(0, 8)}-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error: any) {
      setError(`Failed to download conversation history: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const importConversationFromFile = async (file: File) => {
    if (!sessionId) {
      setError('Please create a session first before importing a conversation');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Read and parse the JSON file
      const fileText = await file.text();
      const conversationData = JSON.parse(fileText);

      // Validate the file is an array
      if (!Array.isArray(conversationData)) {
        throw new Error('Invalid file format. Expected array of messages: [{"role": "...", "content": "..."}, ...]');
      }

      // Import messages to the existing session
      await axios.post(`${API_BASE_URL}/api/v1/sessions/${sessionId}/messages`, {
        messages: conversationData
      });

      // Load messages into UI
      const importedMessages: Message[] = conversationData.map((msg: any) => ({
        id: uuidv4(),
        role: msg.role,
        message_text: msg.content,
        timestamp: new Date(),
        message_type: 'TextMessage',
      }));

      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: 'system',
          message_text: `Imported ${conversationData.length} messages into current session`,
          message_type: 'TextMessage',
          timestamp: new Date()
        },
        ...importedMessages
      ]);

    } catch (error: any) {
      setError(`Failed to import conversation: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importConversationFromFile(file);
    }
  };

  // Listen for incoming messages and analytics events from webhook
  useEffect(() => {
    if (!sessionId) return;

    const eventSource = new EventSource(`${WEBHOOK_BASE_URL}/messages/${sessionId}`);
    
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      // Handle analytics events - add to raw messages tab
      if (data.event_type === 'analytics') {
        console.log('Received analytics event:', data);
        
        const eventName = data.event_name || 'unknown';
        const payload = data.payload || {};
        
        // Extract relevant data based on event type
        let rawMessage: any = {
          event_name: eventName,
          timestamp: data.timestamp || new Date().toISOString(),
        };
        
        if (eventName === 'agent_user_message') {
          // Extract message_text and message_type
          rawMessage.role = 'user';
          rawMessage.message_type = payload.message_type;
          rawMessage.content = payload.message_text;
        } else if (eventName === 'agent_iteration_response') {
          // Extract raw_response and action type (tool, speak, finish, etc.)
          rawMessage.role = 'assistant';
          rawMessage.message_type = payload.action_type ?? 'unknown';
          rawMessage.content = payload.raw_response || '';
        } else if (eventName === 'llm_analytics_response') {
          // Extract LLM analytics response
          rawMessage.role = 'analytics';
          rawMessage.content = `${payload.llm_response || ''}`;
        } else if (eventName === 'memory_snapshot') {
          rawMessage.role = 'analytics';
          rawMessage.content = JSON.stringify(payload.memory_snapshot ?? {}, null, 2);
        } else {
          return; // Skip other analytics events
        }
        
        setRawMessages((prev) => [...prev, rawMessage]);
        return;
      }

      // Skip messages with empty message_text unless they're control/command messages
      if (!data.message_text && data.message_type === 'TextMessage') {
        console.log('Skipping message with empty text:', data);
        return;
      }

      // Handle regular messages
      // Skip user messages since they're already added locally when sent
      if (data.role === 'user') {
        console.log('Skipping user message:', data);
        return;
      }

      console.log('Received assistant message:', data);

      if (data.message_type === 'ActionMessage' && data.action_type === 'divkit') {
        setRawMessages((prev) => [...prev, {
          event_name: 'action_message',
          role: 'assistant',
          message_type: 'divkit',
          content: JSON.stringify(data.payload, null, 2),
          timestamp: data.timestamp || new Date().toISOString(),
        }]);
      }

      const assistantMessage: Message = {
        id: uuidv4(),
        role: data.role || 'assistant',
        message_text: data.message_text || '',
        timestamp: new Date(),
        message_type: data.message_type,
        payload: data.payload,
        action_type: data.action_type,
        command_type: data.command_type,
        control_type: data.control_type,
        sequence_id: data.sequence_id,
        full_content: JSON.stringify(data),
      };
      setMessages((prev: Message[]) => [...prev, assistantMessage]);
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
    };

    return () => {
      eventSource.close();
    };
  }, [sessionId]);


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Tools metadata helper functions
  const addTool = () => {
    setToolsMetadata([...toolsMetadata, {
      name: 'new_tool',
      tools_metadata: {
        'New_Agent': {
          'example_field': 'example_value'
        }
      }
    }]);
  };

  const removeTool = (index: number) => {
    setToolsMetadata(toolsMetadata.filter((_, i) => i !== index));
  };

  const updateToolName = (index: number, name: string) => {
    const updated = [...toolsMetadata];
    updated[index].name = name;
    setToolsMetadata(updated);
  };

  const updateAgentName = (toolIndex: number, oldAgentName: string, newAgentName: string) => {
    const updated = [...toolsMetadata];
    const tool = updated[toolIndex];
    const agentData = tool.tools_metadata[oldAgentName];
    delete tool.tools_metadata[oldAgentName];
    tool.tools_metadata[newAgentName] = agentData;
    setToolsMetadata(updated);
  };

  const addAgentToTool = (toolIndex: number) => {
    const updated = [...toolsMetadata];
    const newAgentName = `Agent_${Object.keys(updated[toolIndex].tools_metadata).length + 1}`;
    updated[toolIndex].tools_metadata[newAgentName] = {
      'example_field': 'example_value'
    };
    setToolsMetadata(updated);
  };

  const removeAgentFromTool = (toolIndex: number, agentName: string) => {
    const updated = [...toolsMetadata];
    const tool = updated[toolIndex];
    if (Object.keys(tool.tools_metadata).length > 1) {
      delete tool.tools_metadata[agentName];
      setToolsMetadata(updated);
    }
  };

  // Helper functions for metadata text handling
  const getMetadataKey = (toolIndex: number, agentName: string) => `${toolIndex}-${agentName}`;

  const getMetadataText = (toolIndex: number, agentName: string, agentData: any) => {
    const key = getMetadataKey(toolIndex, agentName);
    return metadataRawText[key] !== undefined ? metadataRawText[key] : JSON.stringify(agentData, null, 2);
  };

  const updateMetadataText = (toolIndex: number, agentName: string, text: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    setMetadataRawText(prev => ({ ...prev, [key]: text }));
  };

  const applyMetadataChanges = (toolIndex: number, agentName: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    const text = metadataRawText[key];
    if (text !== undefined) {
      try {
        const parsed = JSON.parse(text);
        const updated = [...toolsMetadata];
        updated[toolIndex].tools_metadata[agentName] = parsed;
        setToolsMetadata(updated);
        // Clear the raw text since we've successfully applied it
        setMetadataRawText(prev => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      } catch (error) {
        // Invalid JSON, keep the raw text for editing
        console.log('Invalid JSON, keeping raw text for editing');
      }
    }
  };

  const isMetadataValid = (toolIndex: number, agentName: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    const text = metadataRawText[key];
    if (text === undefined) return true; // No changes made
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  // Helper functions for sessionData text handling
  const getSessionDataText = () => {
    return sessionDataRawText !== '' ? sessionDataRawText : JSON.stringify(sessionData, null, 2);
  };

  const updateSessionDataText = (text: string) => {
    setSessionDataRawText(text);
  };

  const applySessionDataChanges = () => {
    if (sessionDataRawText !== '') {
      try {
        const parsed = JSON.parse(sessionDataRawText);
        setSessionData(parsed);
        // Clear the raw text since we've successfully applied it
        setSessionDataRawText('');
      } catch (error) {
        // Invalid JSON, keep the raw text for editing
        console.log('Invalid JSON, keeping raw text for editing');
      }
    }
  };

  const isSessionDataValid = () => {
    if (sessionDataRawText === '') return true; // No changes made
    try {
      JSON.parse(sessionDataRawText);
      return true;
    } catch {
      return false;
    }
  };

  // Helper functions for agentsOverrides text handling
  const getAgentsOverridesText = () => {
    return agentsOverridesRawText !== null ? agentsOverridesRawText : JSON.stringify(agentsOverrides, null, 2);
  };

  const updateAgentsOverridesText = (text: string) => {
    setAgentsOverridesRawText(text);
  };

  const applyAgentsOverridesChanges = () => {
    if (agentsOverridesRawText === null) return;
    if (agentsOverridesRawText.trim() === '') {
      setAgentsOverrides({});
      setAgentsOverridesRawText(null);
      return;
    }
    try {
      const parsed = JSON.parse(agentsOverridesRawText);
      setAgentsOverrides(parsed);
      setAgentsOverridesRawText(null);
    } catch {
      // Invalid JSON, keep the raw text for editing
    }
  };

  const getResolvedAgentsOverrides = (): {[key: string]: any} => {
    if (agentsOverridesRawText === null) return agentsOverrides;
    if (agentsOverridesRawText.trim() === '') return {};
    try {
      return JSON.parse(agentsOverridesRawText);
    } catch {
      return agentsOverrides;
    }
  };

  const isDivkitMessage = (message: Message) =>
    message.message_type === 'ActionMessage' && message.action_type === 'divkit';

  // Function to render message content based on message type
  const renderMessageContent = (message: Message) => {
    const { message_type, message_text, payload, action_type, command_type, control_type, full_content } = message;

    switch (message_type) {
      case 'TextMessage':
        return message_text;
      
      case 'DataMessage':
        return (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#2196F3' }}>Data Message</div>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 8, 
              borderRadius: 4, 
              fontSize: 12,
              overflow: 'auto',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace'
            }}>
              {JSON.stringify(payload, null, 2)}
            </pre>
          </div>
        );
      
      case 'ActionMessage':
        if (isDivkitMessage(message) && Array.isArray(payload?.data)) {
          return (
            <div>
              {payload.data.map((cardJson: any, index: number) => (
                <DivKitCard key={index} json={cardJson} id={`divkit-${message.id}-${index}`} />
              ))}
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#FF9800' }}>Action: {action_type}</div>
            {payload && (
              <pre style={{ 
                background: '#fff3e0', 
                padding: 8, 
                borderRadius: 4, 
                fontSize: 12,
                overflow: 'auto',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
              }}>
                {JSON.stringify(payload, null, 2)}
              </pre>
            )}
          </div>
        );
      
      case 'UserEvent':
        return (
          <div>
            <div style={{ fontWeight: 600, marginBottom: payload ? 8 : 0, color: '#795548' }}>User Event: {message_text}</div>
            {payload && (
              <pre style={{
                background: '#f5f5f5',
                padding: 8,
                borderRadius: 4,
                fontSize: 12,
                overflow: 'auto',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
              }}>
                {JSON.stringify(payload, null, 2)}
              </pre>
            )}
          </div>
        );

      case 'CommandMessage':
        return (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#9C27B0' }}>Command: {command_type}</div>
          </div>
        );
      
      case 'ControlMessage':
        return (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#607D8B' }}>Control: {control_type}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {control_type === 'SequenceStart' && 'Starting new message sequence'}
              {control_type === 'SequenceFinish' && 'Finishing message sequence'}
            </div>
          </div>
        );
      
      default:
        // Fallback for unknown message types or regular text
        return full_content;
    }
  };

  // ...existing code...
  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto', fontFamily: 'Inter, Arial, sans-serif', backgroundColor: '#f3f5f7', minHeight: '100vh' }}>
      <header style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Voice Agent Test UI</h1>
        <p style={{ margin: '6px 0 0', color: '#666', fontSize: 13 }}>Test interface for the Voice Agent API</p>
      </header>

      {error && (
        <div style={{ marginBottom: 10, color: '#8b0000', background: '#fff0f0', padding: 8, borderRadius: 6 }}>
          {error}
        </div>
      )}

  <main style={{ display: 'flex', gap: 18, alignItems: 'stretch', height: 'calc(100vh - 120px)' }}>
        {/* Left compact sidebar */}
        <aside style={{ width: 320, flexShrink: 0 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 14 }}>Status</strong>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1, background: status.health ? '#e8f6ef' : '#fff4e6', padding: 8, borderRadius: 6, fontSize: 13 }}>
                <div style={{ color: status.health ? '#0b7a3a' : '#a05a00', fontWeight: 600 }}>{status.health ? 'Healthy' : 'Unhealthy'}</div>
                <div style={{ color: '#666', fontSize: 12 }}>Health</div>
              </div>
              <div style={{ flex: 1, background: status.ready ? '#e8f6ef' : '#fff4e6', padding: 8, borderRadius: 6, fontSize: 13 }}>
                <div style={{ color: status.ready ? '#0b7a3a' : '#a05a00', fontWeight: 600 }}>{status.ready ? 'Ready' : 'Not Ready'}</div>
                <div style={{ color: '#666', fontSize: 12 }}>Ready</div>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 14 }}>Session</strong>
              <div style={{ fontSize: 12, color: '#666' }}>{sessionId ? 'Active' : 'Idle'}</div>
            </div>

            {sessionId ? (
              <div style={{ fontSize: 13, color: '#333' }}>
                <div style={{ marginBottom: 8, wordBreak: 'break-all', background: '#f6fff6', padding: 8, borderRadius: 6, fontSize: 12 }}>{sessionId}</div>
                
                {/* Import Conversation into Active Session */}
                <div style={{ marginBottom: 8, padding: 10, background: '#f0f8ff', borderRadius: 6, border: '1px solid #b3d9ff' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#0066cc' }}>
                    Import Conversation
                  </div>
                  <label style={{ 
                    display: 'block',
                    padding: '8px 12px',
                    background: '#fff',
                    border: '1px solid #2196F3',
                    borderRadius: 4,
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: 12,
                    color: '#2196F3',
                    fontWeight: 500
                  }}>
                    📁 Choose JSON File
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleConversationFileUpload}
                      disabled={loading}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div style={{ fontSize: 10, color: '#666', marginTop: 6, textAlign: 'center' }}>
                    Upload a conversation JSON file to add messages to this session
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="button button-danger" onClick={deleteSession} disabled={loading} style={{ flex: 1 }}>
                    {loading ? 'Deleting...' : 'Delete Session'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13 }}>
                {/* Session Type Selector */}
                <div style={{ marginBottom: 12, padding: 10, background: '#f8f9fa', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#555' }}>Session Type</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="sessionType"
                        value="v1"
                        checked={sessionType === 'v1'}
                        onChange={(e) => setSessionType(e.target.value as 'v1' | 'v2' | 'v2-local')}
                      />{' '}
                      V1 (scenario based)
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="sessionType"
                        value="v2"
                        checked={sessionType === 'v2'}
                        onChange={(e) => setSessionType(e.target.value as 'v1' | 'v2' | 'v2-local')}
                      />{' '}
                      V2 (scenario based)
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="sessionType"
                        value="v2-local"
                        checked={sessionType === 'v2-local'}
                        onChange={(e) => setSessionType(e.target.value as 'v1' | 'v2' | 'v2-local')}
                      />{' '}
                      V2 (local config based)
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 13 }}>
                    <input type="checkbox" checked={autorun} onChange={(e) => setAutorun(e.target.checked)} />{' '}
                    Autorun
                  </label>
                </div>

                {/* V1 Specific Fields */}
                {sessionType === 'v1' && (
                  <>
                    <details open style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Scenario Configuration</summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                        <div>
                          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                            Scenario File (JSON)
                          </label>
                          <input
                            type="file"
                            accept=".json"
                            onChange={handleScenarioFileUpload}
                            style={{ fontSize: 12, padding: 6 }}
                          />
                          {scenarioLoaded && scenarioData && (
                            <div style={{ marginTop: 6, padding: 8, background: '#e8f6ef', borderRadius: 4, fontSize: 11 }}>
                              <div style={{ color: '#0b7a3a', fontWeight: 600 }}>✓ Loaded: {scenarioData.name || scenarioData.id}</div>
                              <div style={{ color: '#666', marginTop: 2 }}>Type: {scenarioData.settings?.agentType}</div>
                              <div style={{ color: '#666' }}>Language: {scenarioData.settings?.language}</div>
                            </div>
                          )}
                        </div>
                        <input
                          type="text"
                          value={agentName}
                          onChange={(e) => setAgentName(e.target.value)}
                          placeholder="Agent name (optional)"
                          style={{ padding: 8 }}
                        />
                      </div>
                    </details>

                    <details style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Additional Session Data (Optional)</summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                          Override or supplement scenario defaults with runtime session data:
                        </div>
                        <textarea 
                          rows={6} 
                          value={getSessionDataText()} 
                          onChange={(e) => updateSessionDataText(e.target.value)}
                          onBlur={applySessionDataChanges}
                          placeholder='{"key":"value"}' 
                          style={{ 
                            padding: 8, 
                            border: `1px solid ${isSessionDataValid() ? '#ddd' : '#ff6b6b'}`,
                            backgroundColor: isSessionDataValid() ? 'white' : '#fff5f5',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                          }} 
                        />
                        {!isSessionDataValid() && (
                          <div style={{ fontSize: 11, color: '#ff6b6b', marginTop: 4 }}>
                            Invalid JSON - fix syntax errors
                          </div>
                        )}
                      </div>
                    </details>
                  </>
                )}

                {/* V2 Scenario-based Specific Fields */}
                {sessionType === 'v2' && (
                  <>
                    <details open style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Scenario Configuration</summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                        {/* Toggle between upload and existing scenario */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                          <label style={{ cursor: 'pointer', fontSize: 12 }}>
                            <input
                              type="radio"
                              name="v2ScenarioSource"
                              checked={!useExistingScenarioV2}
                              onChange={() => setUseExistingScenarioV2(false)}
                            />{' '}
                            Upload new scenario file
                          </label>
                          <label style={{ cursor: 'pointer', fontSize: 12 }}>
                            <input
                              type="radio"
                              name="v2ScenarioSource"
                              checked={useExistingScenarioV2}
                              onChange={() => setUseExistingScenarioV2(true)}
                            />{' '}
                            Use existing scenario_id
                          </label>
                        </div>

                        {!useExistingScenarioV2 ? (
                          <div>
                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                              Scenario File (JSON)
                            </label>
                            <input
                              type="file"
                              accept=".json"
                              onChange={handleScenarioFileUploadV2}
                              style={{ fontSize: 12, padding: 6 }}
                            />
                            {scenarioLoadedV2 && scenarioDataV2 && (
                              <div style={{ marginTop: 6, padding: 8, background: '#e8f6ef', borderRadius: 4, fontSize: 11 }}>
                                <div style={{ color: '#0b7a3a', fontWeight: 600 }}>✓ Loaded: {scenarioDataV2.name || scenarioDataV2.id}</div>
                                <div style={{ color: '#666', marginTop: 2 }}>Type: {scenarioDataV2.engineMetadata?.genTools?.settings?.agent_type || 'N/A'}</div>
                                <div style={{ color: '#666' }}>Language: {scenarioDataV2.engineMetadata?.genTools?.settings?.language || 'N/A'}</div>
                                <div style={{ color: '#666' }}>Start Agent: {scenarioDataV2.engineMetadata?.genTools?.settings?.start_agent || 'N/A'}</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                              Scenario ID
                            </label>
                            <input
                              type="text"
                              value={existingScenarioIdV2}
                              onChange={(e) => setExistingScenarioIdV2(e.target.value)}
                              placeholder="Enter existing scenario_id"
                              style={{ padding: 8, fontSize: 12, width: '100%' }}
                            />
                            {existingScenarioIdV2 && (
                              <div style={{ marginTop: 6, padding: 8, background: '#e3f2fd', borderRadius: 4, fontSize: 11 }}>
                                <div style={{ color: '#1976d2', fontWeight: 600 }}>Will use scenario: {existingScenarioIdV2}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>

                    <details style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Additional Session Data (Optional)</summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                          Override or supplement scenario defaults with runtime session data:
                        </div>
                        <textarea 
                          rows={6} 
                          value={getSessionDataText()} 
                          onChange={(e) => updateSessionDataText(e.target.value)}
                          onBlur={applySessionDataChanges}
                          placeholder='{"key":"value"}' 
                          style={{ 
                            padding: 8, 
                            border: `1px solid ${isSessionDataValid() ? '#ddd' : '#ff6b6b'}`,
                            backgroundColor: isSessionDataValid() ? 'white' : '#fff5f5',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                          }} 
                        />
                        {!isSessionDataValid() && (
                          <div style={{ fontSize: 11, color: '#ff6b6b', marginTop: 4 }}>
                            Invalid JSON - fix syntax errors
                          </div>
                        )}
                      </div>
                    </details>

                    <details open style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Agents Overrides (Optional)</summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                        <textarea
                          rows={3}
                          value={getAgentsOverridesText()}
                          onChange={(e) => updateAgentsOverridesText(e.target.value)}
                          onBlur={applyAgentsOverridesChanges}
                          placeholder='e.g. {"agent_name": {"inference_preset": "gemma_27", "agent_class": "CustomAgent"}}'
                          style={{
                            width: '100%',
                            padding: 8,
                            fontSize: 11,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            resize: 'vertical'
                          }}
                        />
                        <div style={{ fontSize: 10, color: '#888' }}>
                          Per-agent config overrides (e.g., agent_class, inference_preset)
                        </div>
                      </div>
                    </details>
                  </>
                )}

                {/* V2-local Specific Fields */}
                {sessionType === 'v2-local' && (
                  <>
                    <details open style={{ fontSize: 13, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Advanced</summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                    <input type="text" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="Language (ru)" style={{ padding: 8 }} />
                    <input type="text" value={startAgent} onChange={(e) => setStartAgent(e.target.value)} placeholder="Start agent (required)" style={{ padding: 8 }} />
                    <textarea 
                      rows={6} 
                      value={getSessionDataText()} 
                      onChange={(e) => updateSessionDataText(e.target.value)}
                      onBlur={applySessionDataChanges}
                      placeholder='{"key":"value"}' 
                      style={{ 
                        padding: 8, 
                        border: `1px solid ${isSessionDataValid() ? '#ddd' : '#ff6b6b'}`,
                        backgroundColor: isSessionDataValid() ? 'white' : '#fff5f5',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                      }} 
                    />
                    {!isSessionDataValid() && (
                      <div style={{ fontSize: 11, color: '#ff6b6b', marginTop: 4 }}>
                        Invalid JSON - fix syntax errors
                      </div>
                    )}
                    <input type="text" value={autoFirstMessage} onChange={(e) => setAutoFirstMessage(e.target.value)} placeholder="Auto first message (optional)" style={{ padding: 8 }} />
                    <div>
                      <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                        Agents Overrides (JSON)
                      </label>
                      <textarea
                        rows={3}
                        value={getAgentsOverridesText()}
                        onChange={(e) => updateAgentsOverridesText(e.target.value)}
                        onBlur={applyAgentsOverridesChanges}
                        placeholder='e.g. {"agent_name": {"inference_preset": "gemma_27", "agent_class": "CustomAgent"}}'
                        style={{
                          width: '100%',
                          padding: 8,
                          fontSize: 11,
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          resize: 'vertical'
                        }}
                      />
                      <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                        Per-agent config overrides (e.g., agent_class, inference_preset)
                      </div>
                    </div>
                  </div>
                </details>

                <details open style={{ fontSize: 13, marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: '#333', marginBottom: 6 }}>Tools Metadata</summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                    {toolsMetadata.map((tool, toolIndex) => (
                      <div key={toolIndex} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10, background: '#fafafa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <label style={{ fontSize: 11, color: '#666', minWidth: 60, fontWeight: 500 }}>Tool Name:</label>
                          <input
                            type="text"
                            value={tool.name}
                            onChange={(e) => updateToolName(toolIndex, e.target.value)}
                            placeholder="Tool name"
                            style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                          />
                          <button
                            onClick={() => removeTool(toolIndex)}
                            style={{ 
                              padding: '6px 10px', 
                              fontSize: 11, 
                              background: '#ff4444', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: 4,
                              cursor: 'pointer',
                              minWidth: 24
                            }}
                          >
                            ×
                          </button>
                        </div>
                        
                        <div style={{ marginLeft: 0 }}>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 6, fontWeight: 500 }}>Agents:</div>
                          {Object.entries(tool.tools_metadata).map(([agentName, agentData], agentIndex) => (
                            <div key={agentIndex} style={{ marginBottom: 10, padding: 8, background: 'white', borderRadius: 4, border: '1px solid #eee' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <label style={{ fontSize: 10, color: '#888', minWidth: 50 }}>Agent:</label>
                                <input
                                  type="text"
                                  value={agentName}
                                  onChange={(e) => updateAgentName(toolIndex, agentName, e.target.value)}
                                  placeholder="Agent name"
                                  style={{ flex: 1, padding: 4, fontSize: 11, border: '1px solid #ddd', borderRadius: 3 }}
                                />
                                <button
                                  onClick={() => removeAgentFromTool(toolIndex, agentName)}
                                  disabled={Object.keys(tool.tools_metadata).length <= 1}
                                  style={{ 
                                    padding: '4px 8px', 
                                    fontSize: 10, 
                                    background: Object.keys(tool.tools_metadata).length <= 1 ? '#ccc' : '#ff6666', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: 3,
                                    cursor: Object.keys(tool.tools_metadata).length <= 1 ? 'not-allowed' : 'pointer',
                                    minWidth: 24
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                              <div style={{ marginTop: 6 }}>
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 4, fontWeight: 500 }}>Metadata (JSON):</div>
                                <textarea
                                  rows={4}
                                  value={getMetadataText(toolIndex, agentName, agentData)}
                                  onChange={(e) => updateMetadataText(toolIndex, agentName, e.target.value)}
                                  onBlur={() => applyMetadataChanges(toolIndex, agentName)}
                                  placeholder='{"field": "value"}'
                                  style={{ 
                                    width: '100%', 
                                    padding: 6, 
                                    fontSize: 10, 
                                    border: `1px solid ${isMetadataValid(toolIndex, agentName) ? '#ddd' : '#ff6b6b'}`, 
                                    borderRadius: 3,
                                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                    resize: 'vertical',
                                    backgroundColor: isMetadataValid(toolIndex, agentName) ? 'white' : '#fff5f5'
                                  }}
                                />
                                {!isMetadataValid(toolIndex, agentName) && (
                                  <div style={{ fontSize: 9, color: '#ff6b6b', marginTop: 2 }}>
                                    Invalid JSON - fix syntax errors
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => addAgentToTool(toolIndex)}
                            style={{ padding: '6px 8px', fontSize: 11, background: '#4CAF50', color: 'white', border: 'none', borderRadius: 4, width: '100%', marginTop: 4 }}
                          >
                            + Add Agent
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={addTool}
                      style={{ padding: '10px', fontSize: 12, background: '#2196F3', color: 'white', border: 'none', borderRadius: 4, fontWeight: 500 }}
                    >
                      + Add Tool
                    </button>
                  </div>
                </details>
                  </>
                )}

                {/* Conversation Pre-load (for all session types) */}
                <div style={{ marginTop: 10, padding: 10, background: '#fff9e6', borderRadius: 6, border: '1px solid #ffd966' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#996600' }}>
                    Pre-load Conversation (Optional)
                  </div>
                  <label style={{ 
                    display: 'block',
                    padding: '8px 12px',
                    background: '#fff',
                    border: '1px solid #FF9800',
                    borderRadius: 4,
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: 12,
                    color: '#FF9800',
                    fontWeight: 500
                  }}>
                    📁 Choose Conversation JSON
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleConversationFileUploadPreSession}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {conversationLoaded && (
                    <div style={{ marginTop: 6, padding: 8, background: '#e8f6ef', borderRadius: 4, fontSize: 11 }}>
                      <div style={{ color: '#0b7a3a', fontWeight: 600 }}>✓ Loaded: {conversationData.length} messages</div>
                      <div style={{ color: '#666', fontSize: 10, marginTop: 2 }}>
                        Will be imported after session creation
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#666', marginTop: 6, textAlign: 'center' }}>
                    Load a conversation to import after session creation
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <button className="button button-primary" onClick={createSession} disabled={loading} style={{ width: '100%' }}>
                    {loading ? 'Creating...' : 'Create Session'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* Right: tabbed area */}
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {sessionId ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
              {/* Tab Navigation */}
              <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e0e0e0', alignItems: 'center' }}>
                <button
                  onClick={() => setActiveTab('chat')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    background: activeTab === 'chat' ? '#2196F3' : 'transparent',
                    color: activeTab === 'chat' ? 'white' : '#666',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: activeTab === 'chat' ? 600 : 400
                  }}
                >
                  Chat
                </button>
                <button
                  onClick={() => setActiveTab('raw')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    background: activeTab === 'raw' ? '#2196F3' : 'transparent',
                    color: activeTab === 'raw' ? 'white' : '#666',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: activeTab === 'raw' ? 600 : 400
                  }}
                >
                  Raw Messages
                </button>
                <div style={{ marginLeft: 'auto', paddingBottom: 8 }}>
                  <button
                    onClick={downloadConversationHistory}
                    disabled={loading}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #4CAF50',
                      background: loading ? '#ccc' : '#4CAF50',
                      color: 'white',
                      borderRadius: 6,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <span>📥</span>
                    {loading ? 'Downloading...' : 'Download History'}
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div style={{ background: '#ffffff', borderRadius: 8, padding: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.04)', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {activeTab === 'chat' ? (
                  <>
                    {/* Filter toggle */}
                    <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontSize: 12, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input 
                          type="checkbox" 
                          checked={showOnlyTextMessages} 
                          onChange={(e) => setShowOnlyTextMessages(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        Show only text messages
                      </label>
                    </div>
                    
                    <div style={{ overflowY: 'auto', flex: 1, paddingRight: 8 }} className="chat-messages">
                {messages
                  .filter(message => !showOnlyTextMessages || message.message_type === 'TextMessage' || message.message_type === 'UserEvent' || isDivkitMessage(message))
                  .map((message) => {
                  const isAssistant = message.role === 'assistant';
                  const isSystem = message.role === 'system';
                  const bubbleStyle: React.CSSProperties = {
                    padding: 12,
                    borderRadius: 10,
                    maxWidth: '75%',
                    fontSize: 14,
                    lineHeight: 1.4,
                    boxShadow: '0 1px 0 rgba(16,24,40,0.03)',
                    border: '1px solid rgba(16,24,40,0.04)'
                  };

                  if (isSystem) {
                    bubbleStyle.background = '#fff7e6';
                    bubbleStyle.border = '1px solid #ffe6a8';
                    bubbleStyle.color = '#4a4a4a';
                    bubbleStyle.maxWidth = '100%';
                  } else if (isAssistant) {
                    bubbleStyle.background = '#eef4ff';
                    bubbleStyle.border = '1px solid #d6e4ff';
                    bubbleStyle.color = '#1f2d4b';
                    if (isDivkitMessage(message)) {
                      // DivKit cards lay themselves out with match_parent widths
                      bubbleStyle.background = '#f2f2f2';
                      bubbleStyle.maxWidth = '100%';
                      bubbleStyle.width = '100%';
                      // no global border-box reset: keep padding inside the bubble
                      bubbleStyle.boxSizing = 'border-box';
                    }
                  } else {
                    bubbleStyle.background = '#e9fff1';
                    bubbleStyle.border = '1px solid #c8f1d8';
                    bubbleStyle.color = '#133724';
                    bubbleStyle.alignSelf = 'flex-end';
                  }

                  const wrapperStyle: React.CSSProperties = { marginBottom: 12, display: 'flex', flexDirection: 'column', alignItems: isAssistant || isSystem ? 'flex-start' : 'flex-end' };

                  return (
                    <div key={message.id} style={wrapperStyle} className={`message message-${message.role}`}>
                      <div style={{ fontSize: 12, color: '#8b93a8', marginBottom: 6 }}>
                        {message.role} - {message.message_type || 'TextMessage'} - {message.timestamp.toLocaleTimeString()}
                      </div>
                      <div style={bubbleStyle}>{renderMessageContent(message)}</div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }} className="chat-input">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={loading || !sessionId}
                  autoFocus
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(16,24,40,0.06)' }}
                />
                <input
                  type="text"
                  value={messageLanguage}
                  onChange={(e) => setMessageLanguage(e.target.value)}
                  placeholder="lang"
                  disabled={loading || !sessionId}
                  style={{ 
                    width: 35, 
                    minWidth: 35,
                    maxWidth: 35,
                    flex: 'none',
                    padding: '10px 4px', 
                    borderRadius: 6, 
                    border: '1px solid rgba(16,24,40,0.06)', 
                    fontSize: 12,
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  className="button button-primary"
                  onClick={handleSend}
                  disabled={loading || !sessionId}
                  style={{ padding: '10px 14px' }}
                >
                  {inputMessage.trim() ? 'Send Message' : 'Send Silence'}
                </button>
              </div>

              <details style={{ marginTop: 8, fontSize: 13 }}>
                <summary style={{ cursor: 'pointer', color: '#666' }}>Send User Event</summary>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}>
                  <input
                    type="text"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    placeholder="event type, e.g. widget.add"
                    disabled={loading || !sessionId}
                    style={{ flex: '0 0 220px', padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(16,24,40,0.06)' }}
                  />
                  <textarea
                    rows={2}
                    value={eventPayloadText}
                    onChange={(e) => setEventPayloadText(e.target.value)}
                    placeholder='payload JSON (optional), e.g. {"product_id": "19445"}'
                    disabled={loading || !sessionId}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: `1px solid ${isEventPayloadValid() ? 'rgba(16,24,40,0.06)' : '#ff6b6b'}`,
                      backgroundColor: isEventPayloadValid() ? 'white' : '#fff5f5',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: 12,
                      resize: 'vertical'
                    }}
                  />
                  <button
                    className="button button-primary"
                    onClick={sendUserEvent}
                    disabled={loading || !sessionId || !eventType.trim() || !isEventPayloadValid()}
                    style={{ padding: '10px 14px' }}
                  >
                    Send Event
                  </button>
                </div>
              </details>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <h3 style={{ margin: 0, fontSize: 16, color: '#333' }}>Raw Messages (Analytics Events)</h3>
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1, paddingRight: 8 }}>
                      {rawMessages.length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                          No raw messages available. Send a message to see the conversation flow.
                        </div>
                      ) : (
                        rawMessages.map((message, index) => (
                          <div key={index} style={{ marginBottom: 16, border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                            <div style={{ background: '#f5f5f5', padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
                              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                <strong>Role:</strong> {message.role} | <strong>Event:</strong> {message.event_name}
                              </div>
                              {message.message_type && (
                                <div style={{ fontSize: 11, color: '#888' }}>
                                  <strong>Message Type:</strong> {message.message_type}
                                </div>
                              )}
                              {message.agent_language && (
                                <div style={{ fontSize: 11, color: '#888' }}>
                                  <strong>Language:</strong> {message.agent_language}
                                </div>
                              )}
                              {message.timestamp && (
                                <div style={{ fontSize: 11, color: '#888' }}>
                                  <strong>Timestamp:</strong> {message.timestamp}
                                </div>
                              )}
                            </div>
                            <div style={{ padding: 12, background: '#fff' }}>
                              <pre style={{ 
                                margin: 0, 
                                fontSize: 12, 
                                lineHeight: 1.4, 
                                color: '#333',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                              }}>
                                {message.content}
                              </pre>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: 20, background: '#fff', borderRadius: 8, boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}>
              <p style={{ margin: 0, color: '#666' }}>No active session. Create a session from the left panel to start the chat.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default App;
